import AddIcon from "@mui/icons-material/Add";
import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import {
  CREATE_TEMPLATES,
  CREATE_PRESCRIPTION_TEMPLATES,
  CREATE_REFERRAL_TEMPLATES,
  CREATE_INSTITUTIONS,
  VIEW_PRESCRIPTION_TEMPLATES,
  VIEW_REFERRAL_TEMPLATES,
  VIEW_INSTITUTIONS,
} from "../../../enums/PermissionTitles";
import { HOME } from "../../../enums/UrlPaths";
import { useRedirectUnauthorized } from "../../../hooks/useRedirectUnauthorized";
import { USER } from "../../../enums/Roles";
import { useIsSmallerScreen } from "../../../utils/Functions";
import TemplatesTab from "./components/TemplatesTab";
import PrescriptionsTab from "./prescriptions/PrescriptionsTab";
import ReferralsTab from "./referrals/ReferralsTab";
import InstitutionsTab from "./institutions/InstitutionsTab";

const TEMPLATES_PARAMS = {
  view: "view",
};

const TEMPLATES_VIEWS = {
  DOCTORS_LETTERS: "doctors-letters",
  PRESCRIPTIONS: "prescriptions",
  REFERRALS: "referrals",
  INSTITUTIONS: "institutions",
};

const ADD_BUTTON_LABELS = {
  [TEMPLATES_VIEWS.DOCTORS_LETTERS]: "AddTemplate",
  [TEMPLATES_VIEWS.PRESCRIPTIONS]: "AddPrescriptionTemplate",
  [TEMPLATES_VIEWS.REFERRALS]: "AddReferralTemplate",
  [TEMPLATES_VIEWS.INSTITUTIONS]: "AddInstitution",
};

/** activeView lives in the URL (?view=), matching Warehouse's WAREHOUSE_TABLE_PARAMS.view convention. */
export default function TemplatesPage() {
  const { t } = useTranslation();
  useRedirectUnauthorized(USER, HOME);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView =
    searchParams.get(TEMPLATES_PARAMS.view) || TEMPLATES_VIEWS.DOCTORS_LETTERS;
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isSmallScreen = useIsSmallerScreen();
  const isViewingPrescriptionsAllowed = isPermissionAllowed(VIEW_PRESCRIPTION_TEMPLATES);
  const isViewingReferralsAllowed = isPermissionAllowed(VIEW_REFERRAL_TEMPLATES);
  const isViewingInstitutionsAllowed = isPermissionAllowed(VIEW_INSTITUTIONS);

  const templatesTabRef = React.useRef(null);
  const prescriptionsTabRef = React.useRef(null);
  const referralsTabRef = React.useRef(null);
  const institutionsTabRef = React.useRef(null);

  const addButtonConfig = {
    [TEMPLATES_VIEWS.DOCTORS_LETTERS]: {
      ref: templatesTabRef,
      visible: isPermissionAllowed(CREATE_TEMPLATES),
    },
    [TEMPLATES_VIEWS.PRESCRIPTIONS]: {
      ref: prescriptionsTabRef,
      visible: isPermissionAllowed(CREATE_PRESCRIPTION_TEMPLATES),
    },
    [TEMPLATES_VIEWS.REFERRALS]: {
      ref: referralsTabRef,
      visible: isPermissionAllowed(CREATE_REFERRAL_TEMPLATES),
    },
    [TEMPLATES_VIEWS.INSTITUTIONS]: {
      ref: institutionsTabRef,
      visible: isPermissionAllowed(CREATE_INSTITUTIONS),
    },
  };
  const activeAddButton = addButtonConfig[activeView];

  function handleViewChange(event, newView) {
    const params = new URLSearchParams(searchParams);
    if (newView === TEMPLATES_VIEWS.DOCTORS_LETTERS) {
      params.delete(TEMPLATES_PARAMS.view);
    } else {
      params.set(TEMPLATES_PARAMS.view, newView);
    }
    setSearchParams(params);
  }

  return (
    <PageContentWrapper sideMargin={isSmallScreen ? 5 : 25} noSideMargin>
      <MUI.Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 3, gap: 2, flexWrap: "wrap" }}
      >
        <MUI.Box>
          <MUI.Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t("Templates")}
          </MUI.Typography>
          <MUI.Typography variant="body2" color="text.secondary">
            {t("DoctorsLetterTemplatesSubtitle")}
          </MUI.Typography>
        </MUI.Box>
        {activeAddButton?.visible && (
          <MUI.Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => activeAddButton.ref.current?.openAddDialog()}
            data-testid="template.page.add-trigger"
            sx={{ borderRadius: 2 }}
          >
            {t(ADD_BUTTON_LABELS[activeView])}
          </MUI.Button>
        )}
      </MUI.Stack>

      <MUI.Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: "divider",
          mb: 3,
          overflow: "hidden",
        }}
      >
        <MUI.Tabs
          value={activeView}
          onChange={handleViewChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            maxWidth: "100%",
            "& .MuiTabs-scroller": {
              overflow: "hidden !important",
            },
          }}
        >
          <MUI.Tab
            label={t("DoctorsLetterTemplates")}
            title={t("DoctorsLetterTemplates")}
            value={TEMPLATES_VIEWS.DOCTORS_LETTERS}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          {isViewingPrescriptionsAllowed && (
            <MUI.Tab
              label={t("PrescriptionsTab")}
              title={t("PrescriptionsTab")}
              value={TEMPLATES_VIEWS.PRESCRIPTIONS}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          )}
          {isViewingReferralsAllowed && (
            <MUI.Tab
              label={t("ReferralsTab")}
              title={t("ReferralsTab")}
              value={TEMPLATES_VIEWS.REFERRALS}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          )}
          {isViewingInstitutionsAllowed && (
            <MUI.Tab
              label={t("InstitutionsTab")}
              title={t("InstitutionsTab")}
              value={TEMPLATES_VIEWS.INSTITUTIONS}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          )}
        </MUI.Tabs>
      </MUI.Paper>

      {activeView === TEMPLATES_VIEWS.DOCTORS_LETTERS && (
        <TemplatesTab ref={templatesTabRef} />
      )}
      {activeView === TEMPLATES_VIEWS.PRESCRIPTIONS && isViewingPrescriptionsAllowed && (
        <PrescriptionsTab ref={prescriptionsTabRef} />
      )}
      {activeView === TEMPLATES_VIEWS.REFERRALS && isViewingReferralsAllowed && (
        <ReferralsTab ref={referralsTabRef} />
      )}
      {activeView === TEMPLATES_VIEWS.INSTITUTIONS && isViewingInstitutionsAllowed && (
        <InstitutionsTab ref={institutionsTabRef} />
      )}
    </PageContentWrapper>
  );
}
